import React, {FC, ReactNode, useEffect, useReducer} from 'react';

import {ConversationModel} from '../../../../models/api/conversationModel';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {RecentCommunicationsBannerContainer} from '../../viewer/conversation/timeline/recentCommunicationsBanner/recentCommunicationsBannerContainer';
import {
  ComposerHeaderBannerDisplayStatusesEnum,
  composerHeaderBannerReducer,
  ComposerHeaderBannerTypesEnum,
  computeCanRenderRecentCommunicationsBanner,
  dismissBanner,
  initialComposerHeaderBannerState,
  setAsCanBeDisplayed,
  setAsNoBanner,
} from './composerHeaderBannerHelpers';

/*
 * Types.
 */

interface ComposerHeaderBannerProps {
  message: DraftMessageModel;
  conversation: ConversationModel;
  teammateId: number;
  hasRecentCommunicationsComposerBanner: boolean;
}

interface BannerRenderer {
  type: ComposerHeaderBannerTypesEnum;
  render: () => ReactNode | undefined;
}

/*
 * Component.
 */

export const ComposerHeaderBanner: FC<ComposerHeaderBannerProps> = (props) => {
  const {message, conversation, teammateId, hasRecentCommunicationsComposerBanner} = props;

  const [composerHeaderBannerState, dispatch] = useReducer(
    composerHeaderBannerReducer,
    initialComposerHeaderBannerState,
  );

  const canRenderRecentCommunicationsBanner = computeCanRenderRecentCommunicationsBanner(
    conversation,
    message,
    hasRecentCommunicationsComposerBanner,
  );

  useEffect(() => {
    if (
      canRenderRecentCommunicationsBanner &&
      composerHeaderBannerState[ComposerHeaderBannerTypesEnum.RECENT_COMMUNICATIONS].displayStatus ===
        ComposerHeaderBannerDisplayStatusesEnum.UNSET
    ) {
      dispatch(setAsCanBeDisplayed(ComposerHeaderBannerTypesEnum.RECENT_COMMUNICATIONS));
    }
  }, [canRenderRecentCommunicationsBanner, composerHeaderBannerState]);

  const recentCommunicationsBannerRenderer: BannerRenderer = {
    type: ComposerHeaderBannerTypesEnum.RECENT_COMMUNICATIONS,
    render: () => (
      <RecentCommunicationsBannerContainer
        conversation={conversation}
        teammateId={teammateId}
        shouldRenderAsComposerBanner
        onEmptyRenderAfterDataFetch={() =>
          dispatch(setAsNoBanner(ComposerHeaderBannerTypesEnum.RECENT_COMMUNICATIONS))
        }
        onClick={() => dispatch(dismissBanner(ComposerHeaderBannerTypesEnum.RECENT_COMMUNICATIONS))}
      />
    ),
  };

  // Listed in order of priority they should be displayed
  const bannerRenderers = [recentCommunicationsBannerRenderer];
  const bannerToRender = bannerRenderers
    .filter(
      (renderer) =>
        composerHeaderBannerState[renderer.type].displayStatus ===
        ComposerHeaderBannerDisplayStatusesEnum.CAN_BE_DISPLAYED,
    )
    .map((renderer) => renderer.render())
    .find((banner) => banner !== undefined);

  if (bannerToRender) {
    return <>{bannerToRender}</>;
  }

  return null;
};
